<template>
  <div class="card p-4 mb-4">
    <DataReviewSection
      :signerFields="signerFields"
      :companyFields="companyFields"
    />

    <ContractList
      :contracts="contracts"
      :isAdmin="isAdmin"
      :documentStatus="matchingDocument?.status ?? 'awaiting_signature'"
      @update:contracts="(updatedContracts) => (contracts = updatedContracts)"
    />
  </div>

  <SignupNavigationButtons
    :showBackButton="fiSignupStore.hasPreviousStep"
    :isStepValid="fiSignupStore.isCurrentStepValid"
    :isLoading="handlingSigningResult || sendingForSigning"
    :showDoneButton="hasSignedDocument"
    :showSignatureButton="!hasSignedDocument"
    @back="fiSignupStore.previous()"
    @start-signing="startSigning"
    @complete="completeSignup"
  />

  <LoadingOverlayComponent v-if="handlingSigningResult || sendingForSigning" />
</template>

<script setup lang="ts">
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { FI_CONTRACTS_WITH_AUTHORIZATION } from "@/signup/fi/data/FiContracts";
import { useFISignupStore } from "@/signup/fiSignupStore";
import { useSignupStore } from "@/signup/signup";
import { Contract, Document } from "@/types";
import { filter, find, intersection, map } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import ContractList from "@/signup/common/ContractList.vue";
import DataReviewSection from "@/signup/common/DataReviewSection.vue";
import SignupNavigationButtons from "@/signup/common/SignupNavigationButtons.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const { globalErrorHandler } = useGlobalHandlers();

const route = useRoute();
const router = useRouter();
const signupStore = useSignupStore();
const { account, company } = storeToRefs(signupStore);
const isAdmin = computed(() => account.value?.admin_account_id !== null);

const fiSignupStore = useFISignupStore();
const sendingForSigning = ref(false);
const handlingSigningResult = ref(false);

const contracts = ref<Contract[]>(FI_CONTRACTS_WITH_AUTHORIZATION);

const includedContracts = computed(() => {
  return filter(contracts.value, { include: true });
});

const matchingDocument = computed(() => {
  const contractKeys = map(
    includedContracts.value,
    ({ contract_key }) => contract_key
  );
  const documents = company.value?.documents ?? [];
  const document = find(documents, ({ contract_keys }: Document) => {
    if (!contract_keys) return false;
    return (
      contractKeys.length === contract_keys.length &&
      intersection(contractKeys, contract_keys).length === contractKeys.length
    );
  });

  return document;
});

const hasSignedDocument = computed(
  () => matchingDocument.value?.status === "completed"
);

const { t } = useI18n();

const signerFields = computed(() => [
  {
    label: t("account.name"),
    value: account.value?.name,
  },
  {
    label: t("account.registry_code"),
    value: account.value?.registry_code,
  },
  {
    label: t("account.phone_number"),
    value: account.value?.phone_number,
  },
]);

const companyFields = computed(() => [
  {
    label: t("company.name"),
    value: company.value?.name,
  },
  {
    label: t("company.registry_code"),
    value: company.value?.registry_code,
  },
  {
    label: t("company.vat"),
    value: company.value?.vat || "-",
  },
  {
    label: t("company.address"),
    value: company.value?.address,
  },
]);

const getAfterSigningRedirectUri = () => {
  const currentUrl = new URL(window.location.href);

  // Clear Dokobit query params
  currentUrl.searchParams.delete("status");
  currentUrl.searchParams.delete("signing_token");
  currentUrl.searchParams.delete("signer_token");

  if (isAdmin.value) {
    currentUrl.searchParams.set("is_admin_doc", "true");
  }

  return currentUrl.toString();
};

const startSigning = async () => {
  try {
    sendingForSigning.value = true;
    const existingSigner = matchingDocument.value?.document_signers?.find(
      (signer) => signer.account_id === account.value?.id
    );

    // Redirect if signing URL exists for the current user on the matching document
    if (existingSigner?.redirect_url) {
      window.location.href = existingSigner.redirect_url;
      return;
    }

    // If no existing signing URL, create a new container
    const url = `/api/document-create-container-for-signing`;
    const response = await goSolidApi.post(url, {
      contracts_in_container: map(
        includedContracts.value,
        ({ contract_key }) => contract_key
      ),
      company_id: company.value?.id,
      source_redirect_uri: getAfterSigningRedirectUri(),
    });

    if (response.data.signing_url) {
      window.location.href = response.data.signing_url;
    } else {
      throw new Error("Failed to get signing URL from backend.");
    }
  } catch (err) {
    globalErrorHandler(err);
  } finally {
    sendingForSigning.value = false;
  }
};

const completeSignup = async () => {
  try {
    handlingSigningResult.value = true;

    // Navigate to dashboard or final destination
    await router.push({
      name: "Dashboard",
      query: { company_id: company.value?.id },
    });
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    handlingSigningResult.value = false;
  }
};

const handleSigningResult = async () => {
  const { status, signing_token, company_id, is_admin_doc } = route.query;
  const shouldCheckSigningResult = status && signing_token && company_id;

  if (!shouldCheckSigningResult) {
    return;
  }

  try {
    handlingSigningResult.value = true;
    const checkUrl = `/api/document-check-signature?status=${status}&signing_token=${signing_token}&company_id=${company_id}`;
    const response = await goSolidApi.get(checkUrl);

    if (response.data?.status !== "completed") {
      console.warn("Signing not completed or check failed:", response.data);
      router.replace({ query: {} }); // Clear query params
      return; // Stay on the page
    }

    // Signing check successful and status is completed

    // Re-initialize the store to fetch updated data
    if (account.value) {
      signupStore.initialize({
        initialAccount: account.value,
        defaultCountry: "FI",
      });
      await signupStore.createCompanyIfMissing();
    } else {
      globalErrorHandler(
        new Error("Account data not available to re-initialize store.")
      );
    }

    // Redirect admin immediately
    if (is_admin_doc === "true") {
      await router.push({
        name: "Dashboard",
        query: { company_id },
      });
      return;
    }

    // Non-admin flow: Send analytics and redirect
    if (typeof window.gtag === "function" && account.value) {
      window.gtag("event", "manual_event_SIGNUP", {
        send_to: "G-MRWP1QB2GY",
        user_data: {
          id: account.value.id,
          email: account.value.email,
          phone_number: account.value.phone_number,
          first_name: account.value.name,
          currency: "EUR",
        },
      });
    }
    await router.push({
      name: "Dashboard",
      query: { company_id },
    });
  } catch (err) {
    globalErrorHandler(err);
    router.replace({ query: {} }); // Clear query params on error
  } finally {
    handlingSigningResult.value = false;
  }
};

onMounted(async () => {
  await handleSigningResult();
});
</script>
